<template lang="pug">
  div
    VeeForm(
      ref="form"
      :validation-schema="formSchema"
      v-slot="{ errors }"
      @submit="submitForm"
      :class="[formItemClass, 'cms-form pt-0 lg:pt-ds-md']"
    )
      div(class="block lg:hidden m-0 md:mb-ds-md w-full")
        AvailabilityRemark(v-if="hasAvailabilityRemark" :block="block")

      div(v-if="hasItemIdentifier" :class="['w-full order-2 px-ds-md md:pr-ds-md mt-ds-ml md:mt-ds-sm mb-ds-sm md:mb-ds-md']")
        LazyItemIdentifier(
          :block="block.innerBlocks[0]"
          :lazy="false")

      div(:class="['w-full md:w-6/12 order-2 px-ds-md md:pr-ds-sm mt-ds-ml md:mt-ds-sm']")
        NewLocationPickupInput(
          :delivery-input="$refs.deliveryInput"
          ref="pickupInput"
          :block="locationBlock"
          :custom-classes="!errors.pickupLocation?.length && !errors.deliveryLocation?.length && hasSetPickupAddress && showFloorSelector ? 'rounded-none rounded-t' : ''"
          :lazy="false"
          :label-position="hasItemIdentifier ? 'animated' : 'border'"
          @focus="focusLocation('pickup')"
          @place="selectLocation")
      div(
        v-if="showFloorSelector"
        class="w-full md:w-6/12 order-3 md:order-4 px-ds-md md:pr-ds-sm"
      )
        SelRefined(
          v-show="hasSetPickupAddress"
          :value="getPickupFloor"
          @input="handlePickupFloor"
          :class="!errors.pickupLocation?.length && !errors.deliveryLocation?.length ? '' : 'mt-ds-sm lg:mt-ds-md'"
          :custom-classes="!errors.pickupLocation?.length && !errors.deliveryLocation?.length ? '!rounded-none !rounded-b !border-t-0' : ''"
          :options="getFloors"
          option-text="name"
          option-value="value"
          indicator
          qa-tag="sel_pickup_floor")

        Checkbox(
          class="mt-ds-md lg:mt-ds-md mb-ds-sm"
          v-show="getPickupFloor !== 'Ground floor'"
          :value="getPickupLiftAvailable"
          @input="handlePickupLiftAvailable"
          :label="$t('location_form.lift_available')"
          qa-tag="sel_pickup_lift")

      div(:class="['w-full md:w-6/12 order-4 md:order-3 px-ds-md md:pl-ds-sm mt-ds-ml md:mt-ds-sm']")
        NewLocationDeliveryInput(
          ref="deliveryInput"
          :block="locationBlock"
          :lazy="false"
          :label-position="hasItemIdentifier ? 'animated' : 'border'"
          :custom-classes="!Object.keys(errors).length && hasSetDeliveryAddress && showFloorSelector ? 'rounded-none rounded-t' : ''"
          @focus="focusLocation('delivery')"
          @place="selectLocation")

      div(
        v-if="showFloorSelector"
        class="w-full md:w-6/12 order-5 md:order-4 px-ds-md md:pl-ds-sm"
      )
        SelRefined(
          v-show="hasSetDeliveryAddress"
          :value="getDeliveryFloor"
          @input="handleDeliveryFloor"
          :options="getFloors"
          :class="!errors.pickupLocation?.length && !errors.deliveryLocation?.length ? '' : 'mt-ds-sm lg:mt-ds-md'"
          :custom-classes="!errors.pickupLocation?.length && !errors.deliveryLocation?.length ? '!rounded-none !rounded-b !border-t-0' : ''"
          option-text="name"
          option-value="value"
          indicator
          qa-tag="sel_delivery_floor")

        Checkbox(
          class="mt-ds-md mb-ds-sm lg:mt-ds-md"
          v-if="getDeliveryFloor !== 'Ground floor'"
          :value="getDeliveryLiftAvailable"
          @input="handleDeliveryLiftAvailable"
          :label="$t('location_form.lift_available')"
          qa-tag="sel_delivery_lift")

      div(
        v-if="showInventorySelector && !hasItemIdentifier"
        :class="{'w-full mt-ds-ml order-5 px-ds-md self-end': true, 'disabled-div': !isValidForm, 'lg:hidden': !hasExtension}")
        div(
          @click="tryToOpenFurnitureItemsModal"
          class="px-ds-md relative flex justify-between lg:justify-start items-center h-ds-xxl border border-mono-200 rounded-md cursor-pointer text-gray-800")
          template(v-if="getItemsCount > 0")
            div(class="-ml-ds-xxxs mr-ds-md mb-ds-sm hidden lg:block")
              Icon.text-blue-primary(
                name="i:search-find"
                :size="24")
            .text-lg.flex-1 {{ $t('items.selection', { count: getItemsCount }) }}
            Icon(
              class="lg:text-blue-primary"
              name="i:pencil-edit"
              :size="20")
          template(v-else)
            div(class="text-lg lg:hidden") {{ $t('items.title') }}
            div(class="lg:hidden")
              Icon(
                name="i:chevron-right-rounded"
                :size="16")
            div(class="-ml-ds-xxxs mr-ds-md mb-ds-sm hidden lg:block")
              Icon.text-blue-primary(
                name="i:search-find"
                :size="24")
            div(class="flex-1 text-lg hidden lg:block") {{ $t('items.search_title') }}
          LazyFurnitureItemsDesktop(
            v-if="!isSmallOrMedium && showFurnitureItems"
            :item-categories="itemCategories"
            @close="hideItems"
            v-click-outside-element="hideItemsOnClickOutside")
        TextInputError.mt-ds-sm(v-if="submittedForm && getInventoryComputed && !getItemsCount")
          | {{ $t('items.validation') }}

      div(class="flex justify-between w-full flex-col-reverse md:flex-row order-6 mt-ds-md p-ds-md pt-0")
        FormFooterUK(
          v-if="$i18n.locale === 'en'"
          :trustpilot-customers="formattedTrustpilotCustomers"
          :trustpilot-rating="formattedTrustpilotRating")

        div(v-if="hasCallUs" class="md:hidden")
          PhoneNumber(
            class="call-us-container"
            :showIcon="true"
            :showButtonText="true"
            :handleClick="handleCallUsClick"
            :buttonText="$t('location_form.call_us')"
          )
          div(class="call-us-description") {{ $t('location_form.tap_to_call') }}

        div(class="grid grid-cols-1 md:grid-cols-[auto,auto] gap-ds-md  items-center w-full")
          div(class="hidden md:block md:col-span-1 overflow-visible min-w-fit max-w-full")
            button(
              @click.prevent="onEmailFormOpen"
              class="av-button-link text-anyvan-cta-secondary"
            ) {{ $t("location_form.already_received_quote") }}

          div(class="w-full md:w-auto md:col-span-1 flex justify-end min-w-fit")
            Btn(
              kind="rebrand-cta"
              size="xl"
              :block="true"
              :wp-block="ctaBlock.innerBlocks[1].innerBlocks[0].innerBlocks[0]"
              :disabled="submittingForm"
              :loading="submittingForm"
              @click="submitForm"
            ).text-rebrandSm {{ getInnerText(ctaBlock.innerBlocks[1]?.innerBlocks[0]?.innerBlocks[0]?.innerHtml) }}

    div(v-if="$i18n.locale !== 'en'")
      FormFooter(
        :block="block"
        :trustpilot-rating="formattedTrustpilotRating"
      )

    div(class="md:hidden w-full mt-ds-sm")
      button(
        @click.prevent="onEmailFormOpen"
        class="av-button-link text-white"
      ) {{ $t("location_form.already_received_quote") }}


    div(class="hidden lg:block w-full")
      AvailabilityRemark(v-if="hasAvailabilityRemark" :block="block")
    teleport(to="body")
      SlideRightTransition
        LazyFurnitureItemsModal(
          v-if="showFurnitureItems && isSmallOrMedium"
          :item-categories="itemCategories"
          @close-modal="showFurnitureItems = false"
          @submit="submitForm(false)")
    teleport(to="body")
      RetrieveQuoteModal(
        v-if="emailFormShow"
        @close-modal="onEmailFormClose")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { mapActions, mapGetters } from "vuex"
import type { ValidationResult } from "vee-validate"
import { object, string } from "yup"
import { toTypedSchema } from "@vee-validate/yup"
import { formatNumberWithLocale } from "~/utils/formatNumberWithLocale"
import axios from "axios"
import { ADD_FLOORS, GET_FLOORS } from "~/store/property"
import { patternCheck } from "~/utils/block"
import {
  GET_LISTING,
  GET_ITEMS_COUNT,
  GET_PICKUP_FLOOR,
  GET_PICKUP_LIFT_AVAILABLE,
  GET_DELIVERY_FLOOR,
  GET_DELIVERY_LIFT_AVAILABLE,
  GET_PICKUP_ADDRESS,
  GET_DELIVERY_ADDRESS,
  SET_PICKUP_FLOOR,
  SET_DELIVERY_FLOOR,
  SET_PICKUP_LIFT_AVAILABLE,
  SET_DELIVERY_LIFT_AVAILABLE
} from "~/store/listing"
import {
  ListingService,
  getV4Url,
  getV4UrlByCategoryId,
  getV4ListingPayload,
  addPreListingIdToSessionStorage,
  isValidLocation
} from "~/services/public/Listing"
import {
  AVAILABILITY_REMARK,
  CALL_US,
  DISABLE_INVENTORY,
  ITEM_IDENTIFIER,
} from "~/constants/blocks"
import { redirectToV2 } from "~/utils/v4-redirect"
import { GET_PHONE_NUMBER_VALUE } from "~/store/phone-number"
import {
  SET_INVENTORY,
  SET_WINDOW_WIDTH,
  GET_INVENTORY_DESKTOP,
  GET_IS_SMALL_OR_MEDIUM,
  GET_WINDOW_WIDTH
} from "~/store/pages"
import { extractHref, getInnerText, getv4LocaleFromI18nLocale } from "~/scripts/useful-functions"
import { ItemService } from "~/services/public/ItemService"
import { FLOOR_GROUND, FLOORS, LIFT_AVAILABLE, LIFT_UNAVAILABLE } from "~/constants/listing"
import { CATEGORY_V4_ID_FURNITURE } from "~/constants/category"
import { LOCALE } from "~/constants/locale"

interface ComponentData {
  submittedForm: boolean
  submittingForm: boolean
  emailFormShow: boolean
  trustpilotCustomers: string
  showFurnitureItems: boolean | null
  initInventory: boolean
  itemCategories: any
  hasExtensionState: boolean
  floors: any[]
  trustpilotRating: number
  waitForGuessPlace: boolean
}

export default defineComponent({
  name: "FurnitureForm",

  props: {
    categoryV4Id: { type: Number, default: CATEGORY_V4_ID_FURNITURE },
    hasExtension: { type: Boolean },
    block: { type: Object as () => Block, required: true }
  },

  data(): ComponentData {
    return {
      submittedForm: false,
      submittingForm: false,
      emailFormShow: false,
      showFurnitureItems: false,
      initInventory: false,
      itemCategories: null,
      hasExtensionState: this.hasExtension,
      floors: FLOORS,
      trustpilotCustomers: "0",
      trustpilotRating: 4.5,
      waitForGuessPlace: false,
    }
  },

  computed: {
    ...mapGetters("pages", {
      getWindowWidth: GET_WINDOW_WIDTH,
      isSmallOrMedium: GET_IS_SMALL_OR_MEDIUM,
      getInventoryDesktop: GET_INVENTORY_DESKTOP
    }),
    ...mapGetters("listing", {
      getListing: GET_LISTING,
      getItemsCount: GET_ITEMS_COUNT,
      getPickupFloor: GET_PICKUP_FLOOR,
      getDeliveryFloor: GET_DELIVERY_FLOOR,
      getPickupLiftAvailable: GET_PICKUP_LIFT_AVAILABLE,
      getDeliveryLiftAvailable: GET_DELIVERY_LIFT_AVAILABLE,
      getPickupAddress: GET_PICKUP_ADDRESS,
      getDeliveryAddress: GET_DELIVERY_ADDRESS
    }),
    ...mapGetters("phone-number", {
      getPhoneNumberValue: GET_PHONE_NUMBER_VALUE
    }),
    ...mapGetters("property", {
      getStoreFloors: GET_FLOORS
    }),

    formSchema() {
      const furnitureFormSchema: Record<string, any> = {
        pickupLocation: string().required(),
        deliveryLocation: string().required()
      }
      if (this.hasItemIdentifier) {
        furnitureFormSchema["itemIdentifier"] = string().required()
      }
      return toTypedSchema(object(furnitureFormSchema))
    },

    isLargeOrAbove(): any {
      return !this.isSmallOrMedium
    },

    locationBlock(): any {
      return this.categoryV4Id ? this.block.innerBlocks[0] : this.block.innerBlocks[1]
    },

    ctaBlock(): any {
      return this.block.innerBlocks[this.block.innerBlocks.length - 1]
    },

    getInventoryComputed(): boolean {
      return this.isSmallOrMedium || this.hasExtensionState
    },

    hasAvailabilityRemark(): boolean {
      return patternCheck(this.block.attributes, AVAILABILITY_REMARK)
    },

    hasItemIdentifier(): boolean {
      return patternCheck(this.block.attributes, ITEM_IDENTIFIER)
    },

    hasCallUs(): boolean {
      return patternCheck(this.block.attributes, CALL_US)
    },

    hasInventory(): boolean {
      return !patternCheck(this.block.attributes, DISABLE_INVENTORY)
    },

    isValidForm(): boolean {
      const listing = this.getListing
      if (!isValidLocation(listing?.pickup) || !isValidLocation(listing?.delivery)) {
        return false
      }

      return true
    },

    hasItemsDesktop(): boolean {
      return this.isLargeOrAbove && this.hasExtensionState && this.getItemsCount
    },

    getFloors(): any[] {
      return this.getStoreFloors || this.floors
    },

    getLiftAvailableOptions(): any[] {
      return [
        {
          name: this.$t("location_form.lift_available"),
          value: LIFT_AVAILABLE
        },
        {
          name: this.$t("location_form.lift_not_available"),
          value: LIFT_UNAVAILABLE
        }
      ]
    },

    hasSetPickupAddress(): boolean {
      return (
        this.getPickupAddress.coordinates.lat !== null &&
        this.getPickupAddress.coordinates.lng !== null
      )
    },

    hasSetDeliveryAddress(): boolean {
      return (
        this.getDeliveryAddress.coordinates.lat !== null &&
        this.getDeliveryAddress.coordinates.lng !== null
      )
    },

    showFloorSelector(): boolean {
      const floorSelectorEnabledCategories = [CATEGORY_V4_ID_FURNITURE]
      const floorSelectorEnabledLocales = [LOCALE.UK, LOCALE.ES, LOCALE.FR, LOCALE.DE]
      return (
        floorSelectorEnabledCategories.includes(this.categoryV4Id) &&
        floorSelectorEnabledLocales.includes(this.$i18n.locale)
      )
    },

    showInventorySelector(): boolean {
      if (!this.hasInventory) {
        return false
      }
      const inventorySelectorEnabledCategories = [CATEGORY_V4_ID_FURNITURE]
      const inventorySelectorEnabledLocales = [LOCALE.UK, LOCALE.ES, LOCALE.FR]
      return (
        inventorySelectorEnabledCategories.includes(this.categoryV4Id) &&
        inventorySelectorEnabledLocales.includes(this.$i18n.locale)
      )
    },

    formattedTrustpilotCustomers(): string {
      return formatNumberWithLocale(this.trustpilotCustomers, this.$i18n.locale)
    },

    formattedTrustpilotRating(): string {
      return formatNumberWithLocale(this.trustpilotRating, this.$i18n.locale)
    },
    formItemClass() {
      const hasFooter = this.$i18n.locale !== 'en'
      return hasFooter ? this.$style.formItemWithFooter : this.$style.formItemNoFooter
    },
  },

  watch: {
    onSubmittingFormChanges(val) {
      if (val) {
        this.$emitter.emit("submitting-location-form")
      } else {
        this.$emitter.emit("submitted-location-form")
      }
    }
  },

  async created() {
    this.$store.dispatch("pages/" + SET_INVENTORY, null)
    const trustPilotConfig = this.$config.public.trustPilot
    let trustPilotId = trustPilotConfig.id
    let trustPilotAPIKey = trustPilotConfig.apiKey

    if (this.$i18n.locale !== LOCALE.UK) {
      const upperCaseLocale = this.$i18n.locale.toUpperCase()
      trustPilotId = trustPilotConfig[`id${upperCaseLocale}`]
      trustPilotAPIKey = trustPilotConfig[`apiKey${upperCaseLocale}`]
    }
    const trustpilotURL = `https://api.trustpilot.com/v1/business-units/${trustPilotId}`
    try {
      const axiosInstance = axios.create() as any
      const response = await axiosInstance.get(trustpilotURL, {
        params: {
          apikey: trustPilotAPIKey
        }
      })
      if (response.status === 200 && response.data) {
        this.trustpilotRating = response.data.score.trustScore
        this.trustpilotCustomers = response.data.numberOfReviews.total
      }
    } catch (err) {
      console.log("Trustpilot error", err)
    }

    const itemsUrl = this.$config.public.v4Host + "/api/v4/booking-flow/config/house-move"
    try {
      const axiosInstance = axios.create() as any
      const headers = {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "AV-locale": getv4LocaleFromI18nLocale(this.$i18n.locale)
        }
      }
      const response = await axiosInstance.get(itemsUrl, headers)
      if (response.status === 200 && response.data) {
        this.addFloors(response.data.propertyFloors.floors)
      }
    } catch (err) {
      console.log("Property config error", err)
    }
  },

  async mounted() {
    if (!this.initInventory) {
      this.initInventory = true
      // Safety mechanism to disable IoLP Desktop when LocationFormExtension is missing
      if (this.hasExtensionState && !this.getInventoryDesktop) {
        this.hasExtensionState = false
      }
      if (this.getInventoryComputed) {
        const itemService = new ItemService(this.$axios, this.$config.public)
        this.itemCategories = await itemService.getFurnitureDefaultInventory(null, this.$i18n.locale)
      }

      const inventoryMobile = this.getInventoryComputed && this.isSmallOrMedium
      await this.$store.dispatch("pages/" + SET_INVENTORY, inventoryMobile)

      // const inventoryDesktop = this.getInventoryComputed && !this.getIsSmallOrMedium
      // if (inventoryDesktop) {
      //   // Enable Sentry Replay tagged with Snowplow Domain UserID
      //   const { getReplay, setTag } = await import("@sentry/vue")
      //   getReplay()?.start()
      //   setTag("sp.domain_userid", this.$snowplow.getSnowplowDomainIds().domain_userid)
      // }

      const ua = navigator.userAgent.toLowerCase()
      // The implemented workaround only works on Safari & Chrome with iOS 16 & iOS 17
      if (
        ua.includes("safari") &&
        navigator.maxTouchPoints > 2 &&
        (ua.includes("version/16") ||
          ua.includes("version/17") ||
          ua.includes("iphone os 16_") ||
          ua.includes("iphone os 17_"))
      ) {
        if (ua.includes("chrome")) {
          document.body.classList.add("chrome")
        } else {
          document.body.classList.add("safari")
        }
      }
    }
  },

  beforeMount() {
    this.$emitter.on("submit-location-form", this.submitForm)
    if (!this.initInventory) {
      window.addEventListener("resize", () => {
        this.setMobileOrDesktop()
      })
    }
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.setMobileOrDesktop)
    this.$emitter.off("submit-location-form")
  },

  methods: {
    ...mapActions("listing", {
      setPickupFloor: SET_PICKUP_FLOOR,
      setDeliveryFloor: SET_DELIVERY_FLOOR,
      setPickupLiftAvailable: SET_PICKUP_LIFT_AVAILABLE,
      setDeliveryLiftAvailable: SET_DELIVERY_LIFT_AVAILABLE
    }),
    ...mapActions("property", {
      addFloors: ADD_FLOORS
    }),

    extractHref,
    getInnerText,

    handlePickupFloor(value: string) {
      if (value === FLOOR_GROUND) {
        this.setPickupLiftAvailable(null)
      }
      if (value) {
        this.setPickupFloor(value)
      }
    },

    handleDeliveryFloor(value: string) {
      if (value === FLOOR_GROUND) {
        this.setDeliveryLiftAvailable(null)
      }
      if (value) {
        this.setDeliveryFloor(value)
      }
    },

    handlePickupLiftAvailable(value: string | null) {
      this.setPickupLiftAvailable(value ? LIFT_AVAILABLE : LIFT_UNAVAILABLE)
    },

    handleDeliveryLiftAvailable(value: string | null) {
      this.setDeliveryLiftAvailable(value ? LIFT_AVAILABLE : LIFT_UNAVAILABLE)
    },

    setMobileOrDesktop() {
      this.$store.dispatch("pages/" + SET_WINDOW_WIDTH, window.innerWidth)
    },

    async tryToOpenFurnitureItemsModal() {
      if (!this.itemCategories) {
        const itemService = new ItemService(this.$axios, this.$config.public)
        this.itemCategories = await itemService.getFurnitureDefaultInventory(null, this.$i18n.locale)
      }
      if (this.isValidForm) {
        this.showFurnitureItems = true
      } else {
        this.$snowplow.trackIOLPButtonDisabled(this.getItemsCount, "Form")
        await (this.$refs.form as any).validate()
      }
    },

    focusLocation(el) {
      this.waitForGuessPlace = true
      if (this.getWindowWidth && this.getWindowWidth > 768) return
      // @ts-ignore
      let element
      switch (el) {
        case "pickup":
          element = this.$refs.pickupInput
          break
        case "delivery":
          element = this.$refs.deliveryInput
          break
        default:
          return
      }
      if (element && element.$el.offsetTop) {
        element.$el.querySelector("input").focus()
        setTimeout(() => {
          window.scrollTo({
            top: element.$el.offsetTop - 25,
            behavior: "smooth"
          })
        }, 300)
      }
    },
    selectLocation() {
      this.waitForGuessPlace = false
    },

    async submitForm(isCalledFromPage = true) {
      if (this.submittingForm) {
        return
      }
      this.submittedForm = true
      this.submittingForm = true

      if (this.waitForGuessPlace) {
        this.waitForGuessPlace = false
        await new Promise(resolve => setTimeout(resolve, 700))
      }

      try {
        const listing = this.getListing

        const isFirstStepValid: ValidationResult = await (this.$refs.form as any).validate()

        let isValid: boolean = isFirstStepValid.valid

        const isSecondStepValid = !this.getInventoryComputed || !!this.getItemsCount || !this.showInventorySelector
        if (isValid) {
          isValid = isSecondStepValid
        }

        if (!isValid && this.showInventorySelector) {
          if (this.getInventoryComputed && isCalledFromPage) {
            const errors = (isFirstStepValid ? "" : "Form") + (isSecondStepValid ? "" : "Items")
            this.$snowplow.trackCTAButtonDisabled("page", this.getItemsCount, errors)
          }
          this.submittingForm = false
          this.$emitter.emit("close-dropdown")
          this.hideItems()
          return
        }

        if (!isValid) {
          this.submittingForm = false
          return
        }

        if (this.getInventoryComputed && isCalledFromPage) {
          this.$snowplow.trackCTAButton("page", this.getItemsCount)
        }

        this.$snowplow.trackButtonClick(
          getInnerText(this.ctaBlock.innerBlocks[1].innerBlocks[0].innerBlocks[0].innerHtml),
          this.$config.public.v4Host + "/" + getV4UrlByCategoryId(this.categoryV4Id)
        )

        if (!this.categoryV4Id) {
          window.location.href =
            this.$config.public.v4Host + "/" + getV4UrlByCategoryId(this.categoryV4Id)
          this.submittingForm = false
          return
        }

        const payload = await getV4ListingPayload(listing, this.categoryV4Id, this.$i18n.locale)

        if (payload.v === 2) {
          redirectToV2(payload, listing, this.$i18n.locale, this.$config.public.v4Host)
          this.submittingForm = false
          return
        }

        const listingService = new ListingService(this.$axios, this.$config)
        const response: ListingResponse = await listingService.postPreListing(
          payload,
          this.$i18n.locale
        )

        if (response?.data && response.data?.pre_listing_id && response.data?.category_id) {
          if (response.is_international) {
            redirectToV2(payload, listing, this.$i18n.locale, this.$config.public.v4Host)
            this.submittingForm = false
            return
          }

          addPreListingIdToSessionStorage(response.data)
          document.addEventListener("visibilitychange", () => {
            this.submittingForm = false
            this.submittedForm = false
            this.showFurnitureItems = false
          })
          window.location.href = getV4Url(
            this.$config.public.v4Host,
            response.data,
            payload,
            this.$i18n.locale
          )
          return
        } else {
          this.submittingForm = false
          if (this.getInventoryComputed) {
            this.$snowplow.trackCTAButtonFailed(
              "page",
              this.getItemsCount,
              "PreListing creation failed"
            )
          }
        }
      } catch (err) {
        this.submittingForm = false
        if (this.getInventoryComputed) {
          this.$snowplow.trackCTAButtonFailed(
            "page",
            this.getItemsCount,
            "PreListing creation error" + err
          )
        }
        console.log("PreListing creation error", err)
      }
    },

    onEmailFormOpen() {
      this.emailFormShow = true
    },

    onEmailFormClose() {
      this.emailFormShow = false
    },

    handleCallUsClick(label = "location_form_button"): void {
      this.$snowplow.trackCallUs({
        phoneNumber: this.getPhoneNumberValue.baseRaw,
        label,
        action: "click",
        extension: this.getPhoneNumberValue.extension
      })
    },

    hideItemsOnClickOutside(event): void {
      // If the user clicked at the Summary or Create/Edit Item dialog then we should not close the dropdown.
      if (event.target.closest(".extension-block") || event.target.closest("#modalContent")) return
      this.$emitter.emit("close-dropdown")
      this.hideItems()
    },

    hideItems(): void {
      setTimeout(() => {
        this.showFurnitureItems = false
      }, 500)
    }
  }
})
</script>

<style lang="scss" module>

.formItem {
  @apply relative flex flex-wrap bg-white;
  margin-top: 24px;

  // Fix the main form sections spacing
  > div[class*="order-"] {
    &.order-2,
    &.order-3,
    &.order-4,
    &.order-5 {
      @apply mb-0;
    }
  }

  :global {
    .av-column-container {
      min-width: max-content;

      .av-button {
        @apply mt-0;
        border-radius: 5px;
      }

      .av-buttons > .av-button {
        @apply mt-0 w-full text-center;
      }
    }

    // Fix button sizing to match inputs
    .av-button.rebrand-cta {
        height: 56px;
        min-height: 56px;


      // Ensure no extra padding is messing with height
      &:not(.loading) {
        @apply py-0;
      }
    }

    .call-us-description {
      @apply text-sm text-center italic mb-ds-xxxs;
    }

    .call-us-container {
      @apply mt-ds-md;
      transition: 0.2s;

      &:hover {
        filter: saturate(1.5) brightness(0.9);
      }

      a {
        @apply flex justify-center items-center w-full h-ds-xxl px-ds-md py-ds-ms font-bold text-rebrandSm text-white no-underline bg-anyvan-cta-secondary border border-black/20;
        border-radius: 8px;
        line-height: normal;

        svg {
          @apply mr-ds-sm;
        }
      }
    }

    .disabled-div {
      & > div:first-child {
        @apply text-gray-600 cursor-not-allowed;
      }
    }
  }
}

@screen md {
  .formItem {
    :global {
      .call-us-description,
      .call-us-container:not(.call-us-cta) {
        @apply hidden;
      }
    }
  }
}
@screen lg {
  .formItem {
    @apply min-w-[487px];
  }
}

.formItemWithFooter {
  composes: formItem;
  border-radius: 6px 6px 0px 0px;
  box-shadow: none;
}

.formItemNoFooter {
  composes: formItem;
  border-radius: 6px;
  box-shadow:
    0 2px 6px 2px rgba(0, 0, 0, 0.15),
    0 1px 2px 0 rgba(0, 0, 0, 0.3);
}
</style>
